<template>
	<template v-if="data">
		<TheForm :model="data" :rules="rules" ref="formRef" :waiting="waiting" :id="formId">
			<ButtonSticky @buttonclick="saveComplaint()" isSubmit>
				<template v-slot:icon><saveIcon /></template>
				<template v-slot:text> Speichern </template>
			</ButtonSticky>

			<section>
				<ButtonBack />

				<el-row class="flex flex--alignstart">
					<el-col :sm="12">
						<h1 :class="data.case ? 'no-margin' : ''">{{ data.title }}</h1>
					</el-col>
					<el-col :sm="12" class="flex flex--justifyend">
						<div :class="`has-status has-status--${$getStateColor('complaint', data.state)}`">
							{{ $getStateFullLabel('complaint', data.state, false) }}
						</div>
					</el-col>
				</el-row>

				<RouterLink :to="'/fall/' + data.case?.id" :title="'Zugeordneten Fall anzeigen'" class="link" v-if="data.case && data.case.id">
					Zugeordneten Fall anzeigen
				</RouterLink>

				<el-row class="button-panel">
					<TheButton type="danger" @buttonclick="openDialogAssignToCase = true" :disabled="!isCaseAssignable">Fall zuweisen</TheButton>
					<TheButton type="danger" @buttonclick="openDialogCreateCaseFromComplaint = true" :disabled="!isCaseAssignable">
						Neuen Fall erstellen
					</TheButton>

					<TheButton type="danger" @buttonclick="openDialogCreateCallback = true" :disabled="!isCallbackPossible">Rückfrage stellen</TheButton>

					<TheButton type="danger" @buttonclick="openDialogNotResponsible = true" :disabled="!setNotResponsiblePossible">Nicht zuständig</TheButton>

					<TheButton type="danger" @buttonclick="openDialogPublishComplaint = true" v-if="!data.publishedDate" :disabled="!isPublishable">
						Beschwerde veröffentlichen
					</TheButton>
					<TheButton type="danger" @buttonclick="openDialogPublishComplaint = true" v-else> Veröffentlichung zurückziehen </TheButton>
				</el-row>

				<TheBox :title="'Beschwerde Infos'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Beschwerdegrund:" prop="reason">
								<el-select v-if="categoryOptions" v-model="selectedCategory" :placeholder="'Bitte wählen'">
									<el-option v-for="option in categoryOptions" :key="option.value?.number" :label="option.label" :value="option.value?.number" />
								</el-select>
							</el-form-item>

							<el-form-item label="Medium:" prop="medium">
								<el-select v-model="selectedMedium" :placeholder="'Bitte wählen'">
									<el-option v-for="option in mediaOptions" :key="option.value?.name" :label="option.label" :value="option.value?.name" />
								</el-select>
							</el-form-item>

							<el-form-item label="Datum:">
								<span>{{ $formatDate(String(data.createdDate)) }}</span>
							</el-form-item>

							<el-form-item label="Rückfragen:">
								<span>{{ data.callbacks.length }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="Anhang:">
								<span>{{ data.files && data.files.length > 0 ? 'Ja' : 'Nein' }}</span>
							</el-form-item>

							<el-form-item label="ID:">
								<span>{{ data.hid }}</span>
							</el-form-item>

							<el-form-item label="Auf Website veröffentlicht:">
								<span v-if="!data.publishedDate">Nein</span>
								<el-date-picker v-else v-model="data.publishedDate" type="date" placeholder="TT.MM.JJJJ" :format="'DD.MM.YYYY'" />
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'Kontakt Beschwerdeführer'" :hasDataGrid="true">
					<el-row>
						<el-col :sm="12" :xs="24">
							<el-form-item label="Anrede:">
								<span>{{ data.gender === 'Male' ? 'Mann' : 'Frau' }}</span>
							</el-form-item>

							<el-form-item label="Vorname:">
								<span>{{ data.firstName }}</span>
							</el-form-item>

							<el-form-item label="Nachname:">
								<span>{{ data.lastName }}</span>
							</el-form-item>
						</el-col>

						<el-col :sm="12" :xs="24">
							<el-form-item label="E-Mail-Adresse:">
								<span>{{ data.email }}</span>
							</el-form-item>

							<el-form-item label="Firma:">
								<span>{{ data.company ? data.company : '-' }}</span>
							</el-form-item>

							<el-form-item label="Telefon:">
								<span>{{ data.phone ? data.phone : '-' }}</span>
							</el-form-item>
						</el-col>
					</el-row>
				</TheBox>

				<TheBox :title="'Beschwerde:'">
					<el-form-item label="" prop="text">
						<el-input v-model="data.text" :autosize="{ minRows: 6 }" type="textarea" placeholder="Bitte Text der Beschwerde hier eingeben." />
					</el-form-item>
				</TheBox>
			</section>

			<section>
				<el-row>
					<el-col :sm="12" :xs="24">
						<h2>Rückfragen</h2>
					</el-col>
					<el-col :sm="12" :xs="24" class="flex flex--justifyend">
						<TheButton @buttonclick="openDialogCreateCallback = true" v-show="!data.publishedDate">
							<el-icon><Plus /></el-icon>Neue Rückfrage
						</TheButton>
					</el-col>
				</el-row>
				<CallbacksTable
					:tableData="data.callbacks ? data.callbacks : []"
					:totalCount="data.callbacks ? data.callbacks.length : 0"
					@loadNextPage="null"
				/>
			</section>

			<DetailsMedia
				:files="complaintFiles"
				:allowDeleteFromServer="true"
				@addToFiles="addToFiles"
				@removeFromFiles="removeFromFiles"
				:pageType="'beschwerde'"
			/>
			<DetailsLinks
				:links="complaintLinks"
				@addToLinks="addToLinks"
				@removeFromLinks="removeFromLinks"
				:pageType="'beschwerde'"
				:allowDeleteFromServer="true"
			/>

			<section>
				<TheBox :title="'Interne Notiz (nur für Mitarbeiter sichtbar)'">
					<el-input v-model="data.internalNote" :autosize="{ minRows: 3 }" type="textarea" placeholder="Interne Notiz" />
				</TheBox>

				<TheBox :id="'log'" :title="'Log'">
					<LogsTable :logs="logs" :loading="logsLoading" />
				</TheBox>

				<TheButton type="default" @buttonclick="openDialogDeleteComplaint = true">
					<el-icon><Delete /></el-icon>
					Beschwerde löschen
				</TheButton>
			</section>
		</TheForm>

		<DialogNotResponsible v-model:open="openDialogNotResponsible" :complaint-id="id" @reload="loadComplaint" />
		<DialogAssignToCase v-model:open="openDialogAssignToCase" :complaint-id="id" @reload="loadComplaint" />
		<DialogCreateCallback v-model:open="openDialogCreateCallback" :complaint-id="id" @reload="loadComplaint" />
		<DialogPublishComplaint
			v-model:open="openDialogPublishComplaint"
			:complaint-id="id"
			:shouldPublishComplaint="!data.publishedDate ? true : false"
			@reload="loadComplaint"
		/>
		<DialogDeleteComplaint v-model:open="openDialogDeleteComplaint" :complaint-id="id" @close="openDialogDeleteComplaint = false" />

		<DialogCreateCaseFromComplaint v-model:open="openDialogCreateCaseFromComplaint" :complaint-id="id" @reload="loadComplaint" />
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import { defineComponent, ref, markRaw } from 'vue';
import { getComplaintLogs, saveComplaint } from '@/requests/complaints';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import CallbacksTable from '../components/tables/CallbacksTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';
import { Plus, Delete, Loading } from '@element-plus/icons-vue';
import {
	AuditResponse,
	ComplaintNotResponsibleRequest,
	ComplaintResponse,
	ComplaintState,
	DetailFile,
	DetailLink,
	FileDto,
	FileResponse,
	LinkDto,
} from '../types/types';
import { returnFormOptions } from '../utils/formOptions.js';
import { texts } from '../utils/texts';
import saveIcon from '../assets/icons/saveIcon.vue';
import { ElMessage } from 'element-plus';
import { SaveComplaintRequest } from '@/types/formdataTypes';
import DialogNotResponsible from '@/components/dialogs/DialogNotResponsible.vue';
import DialogAssignToCase from '@/components/dialogs/DialogAssignToCase.vue';
import DialogCreateCallback from '@/components/dialogs/DialogCreateCallback.vue';
import DialogPublishComplaint from '@/components/dialogs/DialogPublishComplaint.vue';
import DialogDeleteComplaint from '@/components/dialogs/DialogDeleteComplaint.vue';
import DialogCreateCaseFromComplaint from '@/components/dialogs/DialogCreateCaseFromComplaint.vue';
import { getComplaint } from '@/requests/complaints';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import LogsTable from '@/components/tables/LogsTable.vue';
import { validateForm } from '../utils/functions';
import type { FormInstance } from 'element-plus';
import moment from 'moment';
import { getComplaintFiles } from '../utils/fileHandling/files';
import { getComplaintLinks } from '../utils/linkHandling/links';

const ElMessageDuration = 3000;

export default defineComponent({
	name: 'BeschwerdeDetailView',
	data() {
		return {
			data: null as ComplaintResponse | null,
			formId: 'complaintsForm',
			rules: {
				text: [
					{ required: true, message: texts.formErrors.reqMissing, trigger: ['blur'] },
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
				],
			},
			waiting: false,
			saveComplaintRequest: null as SaveComplaintRequest | null,
			shouldPublishComplaint: false,
			loading: true,
			openDialogNotResponsible: false,
			openDialogAssignToCase: false,
			openDialogCreateCallback: false,
			openDialogPublishComplaint: false,
			openDialogDeleteComplaint: false,
			openDialogCreateCaseFromComplaint: false,
			complaintFiles: [] as DetailFile[],
			complaintLinks: [] as DetailLink[],
			logs: [] as AuditResponse[],
			logsLoading: true,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		CallbacksTable,
		Plus,
		Delete,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		Loading,
		DialogNotResponsible,
		DialogAssignToCase,
		DialogCreateCallback,
		DialogPublishComplaint,
		DialogDeleteComplaint,
		DialogCreateCaseFromComplaint,
		LogsTable,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);

		return {
			formRef,
		};
	},
	computed: {
		texts() {
			return texts;
		},
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories');
		},
		mediaOptions() {
			return returnFormOptions('media');
		},
		selectedCategory: {
			get(): string {
				return this.data?.category?.number || '';
			},
			set(value: string) {
				if (this.data) {
					this.data.category = {
						id: '',
						number: value,
						name: '',
					};
				} else return [{ id: '', number: '', name: '' }];
			},
		},
		selectedMedium: {
			get(): string {
				return this.data?.medium?.name || '';
			},
			set(value: string) {
				if (this.data) {
					this.data.medium = {
						id: '',
						name: value,
					};
				} else return [{ id: '', name: '' }];
			},
		},
		isPublishable() {
			if (!this.data) return false;
			if (this.data.isCasePublished || this.data.state === ComplaintState.NotResponsible) return true;
			return false;
		},
		isCaseAssignable() {
			if (!this.data) return false;
			return this.data.state !== ComplaintState.NotResponsible && this.data.state !== ComplaintState.Assigned && !this.data.publishedDate;
		},
		isCallbackPossible() {
			if (!this.data) return false;
			return this.data.state !== ComplaintState.NotResponsible && !this.data.publishedDate;
		},
		setNotResponsiblePossible() {
			if (!this.data) return false;
			return this.data.state !== ComplaintState.NotResponsible && this.data.state !== ComplaintState.Assigned && !this.data.publishedDate;
		},
	},
	methods: {
		async saveComplaint() {
			if (!this.data) return;

			const isValid = await validateForm(this.formRef, this.formId);
			if (!isValid) return;

			this.waiting = true;
			const waitingMsg = ElMessage({ message: texts.formErrors.waiting, type: 'info', duration: 0, showClose: false, icon: markRaw(Loading) });

			const saveComplaintRequest: SaveComplaintRequest = {
				complaintId: this.data.id,
				text: this.data.text,
				category: { number: this.data.category.number },
				medium: { name: this.data.medium.name },
				internalNote: this.data.internalNote,
				publishedDate: this.data.publishedDate,
			};
			const newLinks = this.complaintLinks
				.filter((l) => l.isNew)
				.map((l) => l.link)
				.map((l) => {
					return { name: l.name, url: l.url, type: l.type } as LinkDto;
				});
			if (newLinks.length > 0) saveComplaintRequest.links = newLinks;
			const newFiles = this.complaintFiles
				.filter((f) => f.isNew)
				.map((f) => f.file)
				.map((f) => {
					return { fileName: f.fileName, contentType: f.contentType, bytes: f.bytes } as FileDto;
				});
			if (newFiles.length > 0) saveComplaintRequest.files = newFiles;

			const saveResult = await saveComplaint(saveComplaintRequest);
			if (saveResult.success) {
				this.loadComplaint();

				// setze alle files und links auf isNew = false damit sie nicht doppelt hochgeladen werden
				this.complaintFiles = this.complaintFiles.map((f) => {
					return { file: f.file, isNew: false };
				});

				this.complaintLinks = this.complaintLinks.map((l) => {
					return { link: l.link, isNew: false };
				});

				ElMessage({ message: this.texts.complaints.saveSuccess, type: 'success', duration: ElMessageDuration });
			} else {
				ElMessage({ message: this.texts.complaints.saveFailed, type: 'error', duration: ElMessageDuration });
			}

			this.waiting = false;
			waitingMsg.close();
		},
		async loadComplaint() {
			const loading = startLoading('#beschwerde');

			const result = await getComplaint(this.id);
			if (result.success) {
				this.data = result.data;

				this.complaintFiles = getComplaintFiles(this.data);
				this.complaintLinks = getComplaintLinks(this.data);

				this.loadLogs();
			}

			this.loading = false;
			stopLoading(loading);
		},
		async loadLogs() {
			this.logsLoading = true;
			const result = await getComplaintLogs(this.id);
			if (result.success) {
				this.logs = result.data;
			}

			this.logsLoading = false;
		},
		addToFiles(file) {
			this.complaintFiles.push(file);
		},
		removeFromFiles(file) {
			if (!this.data) return;
			this.complaintFiles = this.complaintFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			if (!this.data) return;
			this.complaintLinks = this.complaintLinks.filter((l) => l.link !== link.link);
		},
		addToLinks(linkObj) {
			this.complaintLinks.push(linkObj);
		},
	},
	mounted() {
		this.loadComplaint();
	},
	watch: {
		'data.publishedDate': {
			handler() {
				if (this.data?.publishedDate) {
					this.data.publishedDate = moment(this.data.publishedDate).toISOString(true);
				}
			},
			immediate: true,
		},
	},
});
</script>
